import React, { useEffect, useState } from "react";
import { useStateContext } from "../../../contexts/ContextProvider";
import { userRequest } from "../../../utils/requestMethods";
import { useHistory } from "react-router-dom";
import SkeletonTable from "../../../components/common/SkeletonTable";
import AlertNotification from "../../../components/common/AlertNotification";
import DataTable from "react-data-table-component";
import { BackButton } from "../../../components";
import { FiSave, FiFileText, FiAlertTriangle } from "react-icons/fi";
import { generateSchoolSessions } from "../../../utils/schoolYearUtils";

const TERM_LABELS = { 1: "First Term", 2: "Second Term", 3: "Third Term" };
const schoolSessions = generateSchoolSessions(2020, 10);

const TeacherUploadClassResult = () => {
  const [submissionErrors, setSubmissionErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { school, user } = useStateContext();
  const history = useHistory();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState([]); // [{ childId, cas: [{ca, score}], exam, total }]
  const [loading, setLoading] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Get assessment system based on subject's level of education
  const getAssessmentSystemForSubject = (subject) => {
    if (!subject?.class?.level_of_education || !school?.settings?.assessment?.assessment_system) {
      return { ca: { cas: [] }, exam: { max_score: 100 } };
    }
    const levelOfEducation = subject.class.level_of_education;
    const assessmentConfig = school.settings.assessment.assessment_system.find(
      (config) => config.level_of_education === levelOfEducation,
    );
    if (assessmentConfig) return assessmentConfig;
    const defaultConfig = school.settings.assessment.assessment_system.find(
      (config) => config.is_default === true,
    );
    return defaultConfig || { ca: { cas: [] }, exam: { max_score: 100 } };
  };

  const currentAssessmentSystem = selectedSubject
    ? getAssessmentSystemForSubject(selectedSubject)
    : { ca: { cas: [] }, exam: { max_score: 100 } };
  const caSettings = currentAssessmentSystem?.ca?.cas || [];
  const examMaxScore = currentAssessmentSystem?.exam?.max_score || 100;
  const currentTerm = school?.settings?.current_term;
  const currentSession = school?.settings?.current_session;
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (!currentTerm || !currentSession) return;
    setSelectedTerm((prev) =>
      prev !== null
        ? prev
        : Number(localStorage.getItem("teacherUploadResult_term")) || currentTerm,
    );
    setSelectedSession((prev) =>
      prev !== null ? prev : localStorage.getItem("teacherUploadResult_session") || currentSession,
    );
  }, [currentTerm, currentSession]);

  // Fetch assigned subjects for the teacher
  useEffect(() => {
    const fetchAssignedSubjects = async () => {
      setLoading(true);
      try {
        const { data } = await userRequest.get(
          `/teachers/teacher-assigned-subjects/${school?._id}/${user?._id}?limit=100`,
        );
        setSubjects(data.subjects || []);
      } catch (error) {
        console.error("Error fetching assigned subjects:", error);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };
    if (school?._id && user?._id) fetchAssignedSubjects();
  }, [school?._id, user?._id]);

  // Fetch students + results when class/subject/term/session change
  useEffect(() => {
    if (!selectedClassId) {
      setStudents([]);
      setResults([]);
      return;
    }
    setStudentLoading(true);
    const fetchStudentsAndResults = async () => {
      try {
        const { data: studentData } = await userRequest.get(
          `children/class/${selectedClassId}?limit=200`,
        );
        setStudents(studentData.children || []);
        if (selectedSubjectId) {
          const { data: resultData } = await userRequest.get(
            `/result/${school?._id}?class_id=${selectedClassId}&subject_id=${selectedSubjectId}&term=${selectedTerm}&session=${selectedSession}&limit=100`,
          );
          const mergedResults = (studentData.children || []).map((student) => {
            const prev = (resultData.data || []).find((r) => r.child?._id === student._id);
            let cas = caSettings.map((_ca, idx) => ({ ca: idx + 1, score: 0 }));
            let exam = 0;
            let total = 0;
            if (prev && Array.isArray(prev.results) && prev.results[0]) {
              cas = caSettings.map((_ca, idx) => {
                const caObj = prev.results[0].cas.find((c) => c.ca === idx + 1);
                return { ca: idx + 1, score: caObj ? caObj.score : 0 };
              });
              exam = prev.results[0].exam || 0;
              total = prev.results[0].total || 0;
            }
            return {
              childId: student._id,
              cas,
              exam,
              total,
              firstname: student.firstname,
              lastname: student.lastname,
            };
          });
          mergedResults.sort((a, b) => (a.lastname || "").localeCompare(b.lastname || ""));
          setResults(mergedResults);
        } else {
          const newResults = (studentData.children || []).map((student) => ({
            childId: student._id,
            cas: caSettings.map((_ca, idx) => ({ ca: idx + 1, score: 0 })),
            exam: 0,
            total: 0,
            firstname: student.firstname,
            lastname: student.lastname,
          }));
          newResults.sort((a, b) => (a.lastname || "").localeCompare(b.lastname || ""));
          setResults(newResults);
        }
      } catch (e) {
        setStudents([]);
        setResults([]);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudentsAndResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, selectedSubjectId, school?._id, selectedTerm, selectedSession]);

  // Partial submission error modal
  const SubmissionErrorModal = ({ errors, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          onClick={onClose}
        >
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <FiAlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">
            Some Results Could Not Be Updated
          </h2>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1.5 mb-5">
          {errors.map((err, idx) => (
            <div key={idx} className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
              {err}
            </div>
          ))}
        </div>
        <button
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] text-white font-semibold text-sm hover:shadow-md transition-all"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedSubjectId || !selectedClassId) {
      setError("Please select a subject.");
      return;
    }
    for (let i = 0; i < results.length; i++) {
      for (let j = 0; j < caSettings.length; j++) {
        const score = results[i].cas[j].score;
        if (score < 0 || score > caSettings[j].max_score) {
          setError(
            `CA ${caSettings[j].title || j + 1} for ${results[i].firstname} ${results[i].lastname} must be between 0 and ${caSettings[j].max_score}`,
          );
          return;
        }
      }
      if (results[i].exam < 0 || results[i].exam > examMaxScore) {
        setError(
          `Exam for ${results[i].firstname} ${results[i].lastname} must be between 0 and ${examMaxScore}`,
        );
        return;
      }
    }
    setLoading(true);
    try {
      const response = await userRequest.post("/result/multiple-result", {
        subjectId: selectedSubjectId,
        schoolId: school?._id,
        session: selectedSession,
        term: selectedTerm,
        classId: selectedClassId,
        results: results.map(({ childId, cas, exam }) => ({ childId, cas, exam })),
      });
      const { message, errors } = response.data || {};
      setSuccess(message || "Results uploaded successfully!");
      setTimeout(() => setSuccess(""), 3000);
      if (Array.isArray(errors) && errors.length > 0) {
        setSubmissionErrors(errors);
        setShowErrorModal(true);
      } else {
        setSubmissionErrors([]);
        setShowErrorModal(false);
      }
    } catch (e) {
      setError("Failed to upload results");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  // DataTable columns
  const caColumns = caSettings.map((ca, caIdx) => ({
    name: ca.title ? `${ca.title} (${ca.max_score})` : `CA ${caIdx + 1} (${ca.max_score})`,
    cell: (_row, rowIndex) => (
      <input
        type="number"
        min={0}
        max={ca.max_score}
        value={results[rowIndex]?.cas[caIdx]?.score ?? ""}
        onChange={(e) => {
          const newResults = [...results];
          newResults[rowIndex].cas[caIdx].score = Number(e.target.value);
          const caSum = newResults[rowIndex].cas.reduce(
            (sum, c) => sum + (Number(c.score) || 0),
            0,
          );
          newResults[rowIndex].total = caSum + (Number(newResults[rowIndex].exam) || 0);
          setResults(newResults);
        }}
        className="w-16 border rounded-lg px-1 py-1 text-center focus:ring-indigo-500 focus:border-indigo-500"
      />
    ),
  }));

  const columns = [
    { name: "Surname", selector: (_row, i) => results[i]?.lastname || "" },
    { name: "First Name", selector: (_row, i) => results[i]?.firstname || "" },
    ...caColumns,
    {
      name: `Exam (${examMaxScore})`,
      cell: (_row, rowIndex) => (
        <input
          type="number"
          min={0}
          max={examMaxScore}
          value={results[rowIndex]?.exam ?? ""}
          onChange={(e) => {
            const newResults = [...results];
            newResults[rowIndex].exam = Number(e.target.value);
            const caSum = newResults[rowIndex].cas.reduce(
              (sum, c) => sum + (Number(c.score) || 0),
              0,
            );
            newResults[rowIndex].total = caSum + (Number(newResults[rowIndex].exam) || 0);
            setResults(newResults);
          }}
          className="w-16 border rounded-lg px-1 py-1 text-center focus:ring-indigo-500 focus:border-indigo-500"
        />
      ),
    },
    {
      name: "Total",
      selector: (_row, i) => results[i]?.total || "",
      cell: (_row, i) => <span className="font-bold">{results[i]?.total || ""}</span>,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#F8FAFC",
        borderBottom: "1px solid #E2E8F0",
      },
    },
    headCells: {
      style: {
        fontSize: "11px",
        fontWeight: "700",
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        borderBottom: "1px solid #F1F5F9",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#F5F9FF",
        borderBottomColor: "#E0E7FF",
      },
    },
    cells: {
      style: {
        paddingTop: "10px",
        paddingBottom: "10px",
      },
    },
  };

  const selectedClassName = selectedSubject?.class?.class_name;

  return (
    <div className="m-2 mt-24 md:mx-10 md:my-0 py-2 md:py-7">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-10 min-h-screen">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-1">
          <BackButton history={history} />
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest">
              Result
            </p>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Upload Class Result</h1>
          </div>
        </div>
        {selectedTerm && selectedSession && (
          <p className="text-sm text-gray-400 mb-6 pl-[52px]">
            {TERM_LABELS[selectedTerm]} &middot; {selectedSession}
          </p>
        )}

        {/* Notifications */}
        <div className="space-y-2 mb-6">
          {error && <AlertNotification type="error" message={error} onClose={() => setError("")} />}
          {success && (
            <AlertNotification type="success" message={success} onClose={() => setSuccess("")} />
          )}
        </div>

        {showErrorModal && (
          <SubmissionErrorModal
            errors={submissionErrors}
            onClose={() => setShowErrorModal(false)}
          />
        )}

        <form onSubmit={handleSubmit}>
          {/* Filter card */}
          <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filters</p>
            </div>
            <div className="p-5 flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Term
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedTerm || ""}
                  onChange={(e) => {
                    const t = Number(e.target.value);
                    setSelectedTerm(t);
                    localStorage.setItem("teacherUploadResult_term", t);
                  }}
                >
                  {[1, 2, 3].map((t) => (
                    <option key={t} value={t}>
                      {TERM_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Session
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedSession || ""}
                  onChange={(e) => {
                    setSelectedSession(e.target.value);
                    localStorage.setItem("teacherUploadResult_session", e.target.value);
                  }}
                >
                  {schoolSessions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Subject
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedSubjectId}
                  onChange={(e) => {
                    const subj = subjects.find((s) => s._id === e.target.value);
                    setSelectedSubjectId(subj?._id || "");
                    setSelectedClassId(subj?.class?._id || "");
                    setSelectedSubject(subj || null);
                  }}
                >
                  <option value="">Select subject</option>
                  {subjects.map((subj) => (
                    <option key={subj._id} value={subj._id}>
                      {subj.subject_name} ({subj.class?.class_name})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results table card */}
          <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2">
              {/* Context info */}
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                {selectedSubject ? (
                  <>
                    {selectedClassName && (
                      <>
                        <span className="text-sm font-semibold text-gray-700 truncate">
                          {selectedClassName}
                        </span>
                        <span className="text-gray-300">&middot;</span>
                      </>
                    )}
                    <span className="text-sm text-gray-500 truncate">
                      {selectedSubject.subject_name}
                    </span>
                    {results.length > 0 && (
                      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                        {results.length} students
                      </span>
                    )}
                  </>
                ) : (
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Results
                  </p>
                )}
              </div>
              {/* Action buttons */}
              <div className="flex gap-2 sm:ml-auto flex-shrink-0">
                <button
                  type="submit"
                  disabled={loading || !selectedSubjectId || !selectedClassId}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-white text-xs bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] hover:from-[#2a2a7a] hover:to-[#2a5bb8] hover:shadow-md hover:scale-[1.02] transition-all duration-200 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-3 h-3" /> Save Results
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3F3DA5]" />
                    <span className="text-sm text-[#3F3DA5] font-medium">Loading...</span>
                  </div>
                </div>
              )}
              <DataTable
                columns={columns}
                data={results}
                noDataComponent={
                  <div className="flex flex-col items-center py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                      <FiFileText className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-500">
                      {!selectedSubjectId
                        ? "Select a subject to load results"
                        : "No students found in this class"}
                    </p>
                  </div>
                }
                customStyles={customStyles}
                progressPending={studentLoading}
                progressComponent={<SkeletonTable rows={5} columns={caSettings.length + 4} />}
                highlightOnHover
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherUploadClassResult;
